import { useParams } from 'react-router-dom';
import type { TimeEntryOverageStatus } from '@catalyst-psa/shared';

import { useAgreementUsagePortal } from './api.js';

const currency = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });

/** "Jun 7, 2026" from an ISO yyyy-mm-dd date (UTC-safe), or "—". */
function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return '—';
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const ALLOCATION_STYLES: Record<TimeEntryOverageStatus, { label: string; className: string }> = {
  included: { label: 'Included', className: 'bg-emerald-100 text-emerald-700' },
  overage: { label: 'Overage', className: 'bg-amber-100 text-amber-700' },
  split: { label: 'Included + overage', className: 'bg-sky-100 text-sky-700' },
};

function AllocationBadge({ status }: { status: TimeEntryOverageStatus }): JSX.Element {
  const style = ALLOCATION_STYLES[status];
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${style.className}`}
    >
      {style.label}
    </span>
  );
}

interface SummaryCardProps {
  label: string;
  value: string;
  tone?: 'default' | 'amber';
}

function SummaryCard({ label, value, tone = 'default' }: SummaryCardProps): JSX.Element {
  return (
    <div
      className={`rounded-lg border p-4 ${
        tone === 'amber' ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-white'
      }`}
    >
      <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
      <dd
        className={`mt-1 text-2xl font-semibold tabular-nums ${
          tone === 'amber' ? 'text-amber-700' : 'text-slate-900'
        }`}
      >
        {value}
      </dd>
    </div>
  );
}

/**
 * Public, unauthenticated managed-services usage portal (/a/:token). Renders the same
 * Cat+ Usage data shown inside the app — prepaid vs overage hours for the current month —
 * gated only by the opaque token in the URL.
 */
export function PublicAgreementUsagePage(): JSX.Element {
  const { token = '' } = useParams<{ token: string }>();
  const viewQuery = useAgreementUsagePortal(token);
  const view = viewQuery.data;

  if (viewQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-500">
        Loading usage…
      </div>
    );
  }

  if (viewQuery.isError || !view) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-8 text-center shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">Link unavailable</h1>
          <p className="mt-2 text-sm text-slate-500">
            This usage link is not available. The link may have been disabled or regenerated.
            Please request a new link.
          </p>
        </div>
      </div>
    );
  }

  const { usage } = view;
  const monthLabel = new Date(`${usage.month}-01T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="mx-auto max-w-5xl px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{view.agreementName}</h1>
            <p className="mt-0.5 text-sm text-slate-500">{view.customerName ?? '—'}</p>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p>Usage for {monthLabel}</p>
          </div>
        </div>

        {/* Summary */}
        <dl className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <SummaryCard label={`Hours used (${monthLabel})`} value={usage.hoursUsedThisMonth} />
          <SummaryCard
            label={usage.isProrated ? 'Hours included (prorated)' : 'Hours included'}
            value={usage.includedHours}
          />
          <SummaryCard
            label="Overage hours this month"
            value={usage.overageHours}
            tone={Number(usage.overageHours) > 0 ? 'amber' : 'default'}
          />
          <SummaryCard
            label="Overage total"
            value={currency.format(Number(usage.overageTotal))}
            tone={Number(usage.overageTotal) > 0 ? 'amber' : 'default'}
          />
        </dl>

        {/* Entries */}
        <div className="mt-6 rounded-lg border border-slate-200 bg-white">
          <div className="border-b border-slate-200 px-3 py-2">
            <span className="text-sm font-medium text-slate-700">
              Hours this month ({monthLabel})
            </span>
          </div>
          {usage.entries.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-500">No hours logged this month.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-3 py-2 font-medium">Date</th>
                  <th className="px-3 py-2 font-medium">Member</th>
                  <th className="px-3 py-2 font-medium">Notes</th>
                  <th className="px-3 py-2 font-medium">Allocation</th>
                  <th className="px-3 py-2 text-right font-medium">Hours</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {usage.entries.map((entry) => (
                  <tr key={entry.id}>
                    <td className="px-3 py-2 text-slate-600">{formatDate(entry.workDate)}</td>
                    <td className="px-3 py-2 text-slate-600">{entry.memberName ?? '—'}</td>
                    <td className="px-3 py-2 text-slate-500">
                      {entry.ticketTitle ?? entry.notes ?? '—'}
                    </td>
                    <td className="px-3 py-2">
                      <AllocationBadge status={entry.status} />
                    </td>
                    <td className="px-3 py-2 text-right font-medium tabular-nums text-slate-800">
                      {entry.hours}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
